import React, { useRef } from "react";
import { Calculator, TriangleAlert, ArrowUpRight } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import BannerCTA from "../CTA/CTABanner.jsx";
import Seo from "../Seo.jsx";
import { OddsMonkeyFT } from '../Affiliate/Om.jsx'
import guidesConfig from "../../config/guidesConfig";
import ScrollModal from "../ui/ScrollModal.jsx";
import smacca from './images/smacca3.gif'

import Tooltip from "./Tooltip";
import "./guides.css";

const LayAccaSmarketsGuide = () => {
  const scrollRef = useRef(null);

  const meta = guidesConfig.layAccaSmarketsGuide?.seo || {};

  return (
    <div className="guides-content">
      <Seo 
        title={meta.title || "How to Lay an Acca on Smarkets"} 
        description={meta.description || "Learn how to lay an accumulator (acca) bet using Smarkets to lock in profit."} 
        url={guidesConfig.layAccaSmarketsGuide?.route || "/guides"} 
      />

      <h2>What is an Accumulator Bet?</h2>
      <p>
        An accumulator bet combines several selections, or <Tooltip content="These are individual bets that are combined to form a multi-selection wager">legs</Tooltip>, into one single bet.
      </p>
      <h6 className="info">To win an acca, every leg must succeed; one losing leg means the entire accumulator loses.</h6>
      <h2>What Methods Are There to Lay Accas?</h2>
    <p>To lay our accas, we can either <span className="key">lock in profit</span> or <span className="key">sequentially lay</span>.</p>
      <p>
      <span className="key">Locking in profit</span> means placing one combined lay bet on the entire accumulator at once. You guarantee your profit, or <span className="key">qualifying loss</span>, no matter what happens.
        </p>
        <p>
      <span className="key">Sequentially laying</span> means placing a separate lay bet on each leg individually—hedging your risk for each match after the last leg ends.
</p>
      <p>Smarkets lets us lay whole accas at once.</p>
<BannerCTA 
        to="/guides/acca-bets-guide" 
        text="Read about accumulator profit strategies" 
        icon={ArrowUpRight} 
      />
<div className="grid-fixed g2">
    <div className="col">
    <h3>Reasons to lock in</h3>
      <ul>
        <li><strong>Simplified Process:</strong> Consolidates the entire lay bet into one transaction.</li>
        <li><strong>Legs can Overlap:</strong> It doesn't matter if events happen at the same time.</li>
        <li><strong>Guaranteed Profit:</strong> Ensures a secured profit, or <span className="key">qualifying loss</span>, even if odds drift.</li>
        <li><strong>Easier to understand:</strong> No advanced calculator needed, can use standard matched betting lay calculators.</li>
      </ul>
    </div>
    <div className="col">
    <h3>Downsides of locking in</h3>
      <ul>
        <li>Smarkets build in their own profit margin, eating further into your profit.</li>
        <li>Your whole <Tooltip content="The amount you risk losing when placing a lay bet">liability</Tooltip> is locked up until the bet settles.</li>
      </ul>
    </div>
</div>
<div className="grid-fixed g2">
    <div className="col">
    <h3>Reasons to sequential lay</h3>
        <ul>
            <li><strong><TriangleAlert/> Avoid Smarkets Eating Your Profit:</strong> Placing each lay bet individually bypasses the extra margins added in by Smarkets.</li>
            <li><strong>Minimise Liability:</strong> We only lay one leg at once, so we don't have to have the maximum <Tooltip content="The amount you risk losing when placing a lay bet">liability</Tooltip> tied up per bet over the whole acca, and you don't have tie any up if an earlier leg has lost.</li>
        </ul>
    </div>
    <div className="col">
    <h3>Downsides of sequential lay</h3>
        <ul>
            <li>Odds may drift (either way) between individual bets.</li>
            <li>More time consuming to lay every leg.</li>
            <li>Risk of human error, such as forgetting to place one of the necessary bets.</li>
            <li>Although there are calculators for this, the underlying maths is complex and hard to follow.</li>
        </ul>
    </div>
</div>
      



    

     

<h2>How to Lay an Acca on Smarkets</h2>

<h3>Step 1: Create the accumulator at the Bookmaker</h3>
<p><strong>Create the accumulator at the bookie.</strong></p>
<h6 className="warn">Do not place the bet yet.</h6>

<h3>Step 2: Create the entire acca at Smarkets</h3>
<p>Select the <span className="back">back</span> odds for each outcome you want to use.</p>

<h6 className="warn">Select the back odds, <em>not</em> the lay odds, even if it feels counter-intuitive.</h6>
<p>At the bottom of the betslip in the <span className="key">multiples</span> section, switch from <span className="key">buy</span> to <span className="key">sell</span>. You have converted it to a lay bet.</p>
<h6 className="info">Smarkets will show you its lay odds which you can use in the next step. They will be a little worse than you might expect, because Smarkets build in a profit margin. To avoid this, sequential lay with o tool like <a href="https://www.oddsmonkey.com/affiliates/affiliate.php?id=66317" target="_blank" rel="noreferrer">OddsMonkey</a>.</h6>
<img src={smacca} alt="How to lay an acca on Smarkets" />

<h3>Step 3: Calculate Lay Stake</h3>
<p>Use the lay stake calculator to work out the right lay stake for the odds offered by Smarkets.</p>

<CTALarge 
  to="/calculators/lay-stake-calculator" 
  text="Go to the Lay Stake Calculator" 
  icon={Calculator} 
/>

<h3>Step 4: Enter your lay stake</h3>
<p>Check Smarkets will let you lay your desired amount.</p>

<h3>Step 5: Place your back bet</h3>
<p>Place your <span className="back">back</span> bet with the bookie, only when you know you can lay the bet.</p>

<h3>Step 6: Lay the acca</h3>
<p>Hit the <span className="key">SELL</span> button to <span className="lay">lay</span> the acca.</p>
<ScrollModal scrollContainerRef={scrollRef} props={{ ctaKey: "om3" }} />

<h2>That's it!</h2>
<p>You now know how to lay a multiple on Smarkets.</p>
<p>
  If you want to learn how to sequential lay to lock in more profit and secure less liability, try using <a href="https://www.oddsmonkey.com/affiliates/affiliate.php?id=66317" target="_blank" rel="noreferrer">OddsMonkey's brilliant tools</a>.
</p>
<p>Matched Edge readers can enjoy a free trial to help you get started and see its value.</p>
<OddsMonkeyFT />
    </div>
  );
};

export default LayAccaSmarketsGuide;
